import { MILESTONES } from "./data/items.js";
import { addDays, daysBetween, dayKey, type DayKey } from "./time.js";
import type { Activity, Area, EffectiveMode, GameState, MuteState, Quest, Size } from "./types.js";

export const SIZE_XP: Record<Size, number> = { S: 10, M: 25, L: 60 };
export const REPEAT_FACTORS = [1, 0.5, 0] as const;
export const REST_PER_DAY = 50;
export const REST_CAP = 250;
export const CAMP_AFTER_DAYS = 2;
export const MESS_MAX = 3;
export const BODY_STAGE_LEVELS = [1, 3, 5, 7, 10] as const;

/**
 * XP required to reach level L: 50 * (L-1)^1.8, rounded. A beginner levels fast (level 2 after ~2 days
 * of one medium thing a day, level 5 after ~3–4 weeks), later each level takes longer (level 10 after
 * ~3–6 months, level 20 after a year or more), like real skills.
 */
export function xpForLevel(level: number): number {
  return level <= 1 ? 0 : Math.round(50 * Math.pow(level - 1, 1.8));
}

export function levelForXp(xp: number): number {
  let level = 1;
  // Small epsilon guards float noise at exact thresholds (e.g. 800 for lv 5).
  while (xp + 1e-9 >= xpForLevel(level + 1)) level++;
  return level;
}

export function levelProgress(xp: number): { level: number; into: number; span: number } {
  const level = levelForXp(xp);
  const lo = xpForLevel(level);
  const hi = xpForLevel(level + 1);
  return { level, into: xp - lo, span: hi - lo };
}

/** Body stage 0-4 from the Body level (1-2, 3-4, 5-6, 7-9, 10+). */
export function bodyStage(bodyLevel: number): number {
  let s = 0;
  for (let i = 0; i < BODY_STAGE_LEVELS.length; i++) if (bodyLevel >= BODY_STAGE_LEVELS[i]!) s = i;
  return s;
}

export function normalizeText(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^\p{L}\p{N} ]/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}

const QUEST_STOPWORDS = new Set([
  "przed",
  "dzisiaj",
  "dzis",
  "jutro",
  "rano",
  "wieczorem",
  "zrobic",
  "zrobil",
  "zrobilem",
  "trzeba",
  "musze",
]);

/** Content words of a quest or activity: words of 4+ letters cut to a 4-letter stem, and numbers. */
function stems(s: string): Set<string> {
  const words = normalizeText(s).normalize("NFD").replace(/\p{M}/gu, "").replace(/ł/g, "l").split(" ");
  const out = new Set<string>();
  for (const w of words) {
    if (/^\d+$/.test(w)) out.add(w);
    else if (w.length >= 4 && !QUEST_STOPWORDS.has(w)) out.add(w.slice(0, 4));
  }
  return out;
}

/**
 * Does a logged activity look like this quest? At least two thirds of the quest's content words
 * (Polish forms differ, so 4-letter stems) appear in the activity: "Wynieść śmieci" ~ "wyniosłem śmieci",
 * "10 pompek przed snem" ~ "zrobiłem 10 pompek", but not "posprzątać łazienkę" ~ "posprzątałem kuchnię".
 */
export function matchesQuest(questTitle: string, activityText: string): boolean {
  const q = stems(questTitle);
  if (!q.size) return false;
  const a = stems(activityText);
  let hit = 0;
  for (const w of q) if (a.has(w)) hit++;
  return hit * 3 >= q.size * 2;
}

/** How many times this activity (same area and text, or same quest) was already logged that day. */
export function repeatCount(
  recent: readonly Activity[],
  day: DayKey,
  area: Area,
  text: string,
  questId: string | null,
): number {
  const n = normalizeText(text);
  return recent.filter(
    (a) =>
      a.day === day && a.area === area && ((questId && a.questId === questId) || normalizeText(a.text) === n),
  ).length;
}

export function baseXp(size: Size, repeats: number): number {
  return Math.round(SIZE_XP[size] * REPEAT_FACTORS[Math.min(repeats, REPEAT_FACTORS.length - 1)]!);
}

/**
 * Rested pool available today. Every break day from the second one adds 50, capped at 250.
 * Example: active Monday, back on Thursday -> Tuesday and Wednesday were breaks -> +50.
 */
export function restedPool(stats: { rested: number; lastActiveDay: DayKey | null }, today: DayKey): number {
  if (!stats.lastActiveDay) return Math.min(REST_CAP, stats.rested);
  const breakDays = Math.max(0, daysBetween(stats.lastActiveDay, today) - 1);
  const accrued = REST_PER_DAY * Math.max(0, breakDays - 1);
  return Math.min(REST_CAP, stats.rested + accrued);
}

/** Rested XP doubles gains while the pool lasts. */
export function applyRested(base: number, pool: number): { xp: number; bonus: number; pool: number } {
  const bonus = Math.min(pool, base);
  return { xp: base + bonus, bonus, pool: pool - bonus };
}

export function goldForXp(xp: number): number {
  return Math.round(xp / 2);
}

export function idleDays(lastActiveDay: DayKey | null, today: DayKey): number {
  return lastActiveDay ? Math.max(0, daysBetween(lastActiveDay, today)) : 0;
}

/** Active days within the 7-day window ending today (no streaks). */
export function consistency(recent: readonly Activity[], today: DayKey): number {
  const from = addDays(today, -6);
  const days = new Set<string>();
  for (const a of recent) if (a.day >= from && a.day <= today) days.add(a.day);
  return days.size;
}

export function modeActive(state: Pick<GameState, "mode">, today: DayKey): GameState["mode"]["kind"] {
  const m = state.mode;
  if (m.kind !== "normal" && m.until && today >= m.until) return "normal";
  return m.kind;
}

export function effectiveMode(state: Pick<GameState, "mode" | "stats">, today: DayKey): EffectiveMode {
  const kind = modeActive(state, today);
  if (
    kind === "normal" &&
    state.stats.lastActiveDay &&
    idleDays(state.stats.lastActiveDay, today) >= CAMP_AFTER_DAYS
  )
    return "camp";
  return kind;
}

export function activeMute(mute: MuteState | null, now: number): MuteState | null {
  return mute && now < mute.until ? mute : null;
}

/** Messy apartment: overdue open Home quests, max 3 levels. Never in sick, vacation or muted-for-days. */
export function messLevel(state: GameState, now: number): number {
  const tz = state.settings.timezone;
  const today = dayKey(now, tz);
  const mode = modeActive(state, today);
  if (mode === "sick" || mode === "vacation") return 0;
  if (activeMute(state.mute, now)?.kind === "days") return 0;
  const overdue = state.quests.filter((q) => q.status === "open" && q.area === "home" && q.dueDay < today);
  return Math.min(MESS_MAX, overdue.length);
}

export function levels(xp: Record<Area, number>): Record<Area, number> {
  return {
    body: levelForXp(xp.body),
    mind: levelForXp(xp.mind),
    work: levelForXp(xp.work),
    social: levelForXp(xp.social),
    home: levelForXp(xp.home),
  };
}

/** Milestone furniture earned for given levels. */
export function earnedMilestones(lv: Record<Area, number>): string[] {
  return MILESTONES.filter((m) => lv[m.area] >= m.level).map((m) => m.id);
}

/** Items to draw: owned items minus ones replaced by a better version. */
export function visibleItems(owned: readonly string[]): string[] {
  const replaced = new Set(
    MILESTONES.filter((m) => m.replaces && owned.includes(m.id)).map((m) => m.replaces as string),
  );
  return owned.filter((id) => !replaced.has(id));
}

export function openQuests(quests: readonly Quest[]): Quest[] {
  return quests.filter((q) => q.status === "open");
}

/** Open quests due today or earlier, oldest first. */
export function dueQuests(quests: readonly Quest[], today: DayKey): Quest[] {
  return openQuests(quests)
    .filter((q) => q.dueDay <= today)
    .sort((a, b) => (a.dueDay < b.dueDay ? -1 : a.dueDay > b.dueDay ? 1 : 0));
}
